import { observable, flow, toJS } from 'mobx';
import API from 'api';
import Query from 'utilities/Query';
import * as yup from 'yup';
import { action, IObservableArray } from 'mobx/lib/mobx';
import PracticeProgram from 'stores/PracticeProgram';
import TherapeuticArea from 'stores/TherapeuticArea';
import _ from 'lodash';

export default class Practice {
	@observable id: number;
	@observable practiceCode: string = '';
	@observable practiceName: string = '';
	@observable practiceAddress: string = '';
	@observable practiceSuburb: string = '';
	@observable practicePostcode: string;
	@observable practiceState: string = '';
	@observable practicePhone: string = '';
	@observable practiceEmail: string = '';
	@observable practiceFAX: string = '';
	@observable practiceContact: string = '';
	@observable practiceType: string = '';
	@observable practicePrivate: boolean = false;
	@observable practiceProviderNo: string = '';
	@observable therapeuticAreaID: number;
	@observable created: string;
	@observable modified: string;
	@observable disabled: boolean = false;
	@observable scheduledReminders: boolean = false;
	@observable TherapeuticArea: TherapeuticArea = new TherapeuticArea();
	@observable PracticePrograms: IObservableArray<PracticeProgram> = [];

	constructor(data: JSON = null) {
		if (data) this.extend(data);
	}

	extend(data: JSON) {
		Object.assign(this, data);
	}

	static loadById = async (id: number): Promise<Practice> => {
		let res = await API.get(
			`/Practices/${id}?filter=${JSON.stringify({
				include: ['PracticePrograms']
			})}`
		);
		return new Practice(res);
	};

	@action
	reload = async () => {
		let res = await Practice.loadById(this.id);
		this.extend(toJS(res));
	};

	update = flow(function*(withData) {
		if (withData) this.extend(withData);
		this.modified = new Date();
		if (this.id) {
			// Update record
			yield API.patch(`/Practices/${this.id}`, toJS(this));
		} else {
			// New record
			this.created = new Date();
			let result = yield API.post(`/Practices`, toJS(this));
			this.id = result.id;
		}
		yield Promise.all(
			this.PracticePrograms.map(pp => {
				pp.practiceID = this.id;
				return new PracticeProgram(pp).update();
			})
		);
	});

	static loadPractices(query?: Query): Promise<{ itemCount: number, items: Array<Practice> }> {
		if (!query) {
			query = new Query();
			query.pageSize = 1000;
		}
		return new Promise((resolve, reject) => {
			API.get(`/Practices/practices/${query.toToLpQueryUrl()}`)
				.then(result => {
					let res = {
						itemCount: result.count,
						items: result.items.map(el => new Practice(el))
					};
					resolve(res);
				})
				.catch(err => {
					reject(err);
				});
		});
	}

	static loadPracticeDDLByUser = async (accountID: number): Promise<Array<object>> => {
		return new Promise((resolve, reject) => {
			API.get(
				`/AccountPrescriberPractices?filter=${JSON.stringify({
					where: { and: [{ accountID: accountID }, { disabled: false }] },
					include: {
						relation: 'PrescriberPractice',
						scope: {
							fields: ['id', 'practiceID', 'disabled'],
							include: {
								relation: 'Practice',
								scope: {
									fields: ['id', 'practiceName', 'scheduledReminders', 'disabled']
								}
							}
						}
					}
				})}`
			)
				.then(res => {
					const filtered = _.filter(
						res,
						o =>
							!o.PrescriberPractice?.disabled &&
							!o.PrescriberPractice?.Practice?.disabled &&
							!!o.PrescriberPractice?.Practice?.scheduledReminders
					);
					let el;
					let practices = filtered.map((o, idx) => {
						el = o.PrescriberPractice?.Practice;
						return { key: idx + 1, value: el.id, text: el.practiceName };
					});
					practices = _.uniqBy(practices, 'value');
					practices = _.sortBy(practices, 'practiceName');
					resolve(practices);
				})
				.catch(err => {
					reject(err);
				});
		});
	};
}



// WEBPACK FOOTER //
// ./src/stores/Practice.js